import EastIcon from '@mui/icons-material/East'
import { AlertColor, Box, Container, Grid, ThemeProvider, Typography, createTheme } from '@mui/material'
import { EditProfileDTO } from '@src/api/@types/EditProfileDTO'
import { useGetProfile, useSubmitProfile } from '@src/api/profile-api'
import { CustomButton, CustomSnackbar, PageSpinner, TitleSection } from '@src/components'
import { formatAddress } from '@src/services/Formatter'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import TextFields from './components/TextFields'

const theme = createTheme({
  components: {
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-input': {
            color: 'white',
          },
          '& .MuiInputLabel-root': {
            color: 'gray',
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: 'white',
          },
          '& .MuiInput-underline:after': {
            borderBottomColor: 'white',
          },
        },
      },
    },
  },
})

const styles = {
  textField: {
    marginTop: '16px',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  button: {
    marginTop: '1rem',
    marginBottom: '1rem',
  },
}

const Profile = () => {
  const { t } = useTranslation()

  const [profileData, isLoading] = useGetProfile()
  const [submitProfile] = useSubmitProfile()

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  })

  const [profile, setProfile] = useState({
    address: '',
    email: '',
    phoneNumber: '',
  })

  const [initialProfile, setInitialProfile] = useState({
    address: '',
    email: '',
    phoneNumber: '',
  })

  const [addressError, setAddressError] = useState<string | null>(null)
  const [emailError, setEmailError] = useState<string | null>(null)
  const [phoneError, setPhoneError] = useState<string | null>(null)
  const [isChanged, setIsChanged] = useState(false)

  useEffect(() => {
    if (profileData) {
      const initialProfileData = {
        address: formatAddress(profileData),
        email: profileData.emailAddress,
        phoneNumber: profileData.cellPhoneNumber,
      }
      setProfile(initialProfileData)
      setInitialProfile(initialProfileData)
    }
  }, [profileData])

  const handleInputChange = (field: keyof EditProfileDTO, value: string) => {
    setProfile((prev) => {
      const updatedProfile = { ...prev, [field]: value }
      setIsChanged(
        updatedProfile.address !== initialProfile.address ||
        updatedProfile.email !== initialProfile.email ||
        updatedProfile.phoneNumber !== initialProfile.phoneNumber
      )
      return updatedProfile
    })
  }

  const handleCloseSnackbar = () => {
    setSnackbar((snak) => ({
      ...snak,
      open: false,
    }))
  }

  const handleSaveChanges = async () => {
    if (addressError || emailError || phoneError || !profile.address || !profile.email || !profile.phoneNumber) {
      setSnackbar({
        open: true,
        message: t('snackbar.validationErrors'),
        severity: 'error',
      })
      return
    }

    try {
      await submitProfile(profile)
      setSnackbar({
        open: true,
        message: t('userInfo.successMesage'),
        severity: 'success',
      })
      setInitialProfile(profile)
      setIsChanged(false)
    } catch (error) {
      setSnackbar({
        open: true,
        message: t('snackbar.profileUpdateFail', { message: (error as Error).message }),
        severity: 'error',
      })
    }
  }

  if (isLoading) return <PageSpinner isLoading={true} />

  const addressValue = profile?.address ?? ''
  const emailValue = profile?.email ?? ''
  const phoneNumberValue = profile?.phoneNumber ?? ''

  return (
    <ThemeProvider theme={theme}>
      {profileData && (
        <>
          <TitleSection title={t('menus.profile')} profileData={profileData} />

          <Container component="main" sx={{ flexGrow: 1 }}>
            <Grid container>
              <Grid size={{ xs: 12, md: 9, lg: 9 }}>
                <Box sx={{ padding: { xs: '0 1px', sm: '0 20px' }, boxSizing: 'border-box' }}>
                  <Typography variant="body1" color="#ccc" marginBottom="1rem">
                    {t('userInfo.myinformation')}
                  </Typography>

                  <TextFields
                    handleInputChange={handleInputChange}
                    styles={styles}
                    addressValue={addressValue}
                    emailValue={emailValue}
                    phoneNumberValue={phoneNumberValue}
                    addressError={addressError}
                    setAddressError={setAddressError}
                    emailError={emailError}
                    setEmailError={setEmailError}
                    phoneError={phoneError}
                    setPhoneError={setPhoneError}
                  />
                  <CustomButton
                    title={t('payment.applyChanges')}
                    onClick={handleSaveChanges}
                    Icon={EastIcon}
                    disabled={!isChanged || addressError !== null || emailError !== null || phoneError !== null || !profile.address || !profile.email || !profile.phoneNumber}
                  />
                  <CustomSnackbar
                    open={snackbar.open}
                    message={snackbar.message}
                    severity={snackbar.severity as AlertColor}
                    onClose={handleCloseSnackbar}
                  />
                </Box>
              </Grid>
            </Grid>
          </Container>
        </>
      )}
    </ThemeProvider>
  )
}

export default Profile
